import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDemandMetrics,
  getDiscomDrawalScheduleMetrics,
  getTotalDrawalScheduleMetrics,
} from "../../../Rest_api/DemoRestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import { useDispatch, useSelector } from "react-redux";

import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../../Rajasthan/const";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { lightenColor } from "../../../components/recharts/ReMixCharts";
import { stringToConsistentColor } from "../../../extensions/string";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];

const DISCOM = [
  { label: "Discom 1", key: "Discom 1" },
  { label: "Discom 2", key: "Discom 2" },
  { label: "Discom 3", key: "Discom 3" },
];

const TYPE = ["Schedule", "Drawal"];

interface RajasthanSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  metrics: DiscomLoadMetrics | null;
}

export function RajasthanSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: RajasthanSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [discomData, setDiscomData] = useState<any[]>([]);
  const [totalDiscomData, setTotalDiscomData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ▶ UPDATED — Promise.all (from original)
  const fetchSummaryData = useCallback(async () => {
    setLoading(true);

    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const [rvpnData, discomMetrics, totalDiscomMetrics] = await Promise.all([
        getRajasthanDemandMetrics(startDateStr, endDateStr),
        getDiscomDrawalScheduleMetrics(startDateStr, endDateStr),
        getTotalDrawalScheduleMetrics(startDateStr, endDateStr),
      ]);

      setSummaryData(Array.isArray(rvpnData) ? rvpnData : []);
      setDiscomData(Array.isArray(discomMetrics) ? discomMetrics : []);
      setTotalDiscomData(
        Array.isArray(totalDiscomMetrics) ? totalDiscomMetrics : [],
      );
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
      setDiscomData([]);
      setTotalDiscomData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    if (selectedGroupKeys === "RVPN Drawal") {
      const processedData = summaryData.map((item) => ({
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time:
          item.slot && item.time_string
            ? `${item.slot} (${item.time_string})`
            : item.slot,
        min_demand: item.min_demand * conversionFactor,
        max_demand: item.max_demand * conversionFactor,
        avg_demand: item.avg_demand * conversionFactor,
        mode_demand_window_formula:
          item.mode_demand_window_formula * conversionFactor,
        min_demand_date: item.date_of_min,
        max_demand_date: item.date_of_max,
      }));

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);
      setChartKeys([]);
    } else if (selectedGroupKeys === "Total Discom Load") {
      // ▶ KEPT demo-only — aggregate from discomData when discom filter active
      let dataToProcess = totalDiscomData;

      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        const slotAggregates = new Map<number, any>();
        discomData
          .filter((item) => selectedDiscom.includes(item.discom))
          .forEach((item) => {
            const slot = item.slot;
            if (!slotAggregates.has(slot)) {
              slotAggregates.set(slot, {
                slot,
                time_string: item.time_string,
                min_drawal: 0,
                max_drawal: 0,
                avg_drawal: 0,
                mode_drawal_window_formula: 0,
                min_schedule: 0,
                max_schedule: 0,
                avg_schedule: 0,
                mode_schedule_window_formula: 0,
              });
            }
            const agg = slotAggregates.get(slot);
            agg.min_drawal += item.min_drawal || 0;
            agg.max_drawal += item.max_drawal || 0;
            agg.avg_drawal += item.avg_drawal || 0;
            agg.mode_drawal_window_formula +=
              item.mode_drawal_window_formula || 0;
            agg.min_schedule += item.min_schedule || 0;
            agg.max_schedule += item.max_schedule || 0;
            agg.avg_schedule += item.avg_schedule || 0;
            agg.mode_schedule_window_formula +=
              item.mode_schedule_window_formula || 0;
          });
        dataToProcess = Array.from(slotAggregates.values());
      }

      const processedData = dataToProcess.map((item) => ({
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time:
          item.slot && item.time_string
            ? `${item.slot} (${item.time_string})`
            : item.slot,
        min_drawal: (item.min_drawal || 0) * conversionFactor,
        max_drawal: (item.max_drawal || 0) * conversionFactor,
        avg_drawal: (item.avg_drawal || 0) * conversionFactor,
        mode_drawal_window_formula:
          (item.mode_drawal_window_formula || 0) * conversionFactor,
        min_schedule: (item.min_schedule || 0) * conversionFactor,
        max_schedule: (item.max_schedule || 0) * conversionFactor,
        avg_schedule: (item.avg_schedule || 0) * conversionFactor,
        mode_schedule_window_formula:
          (item.mode_schedule_window_formula || 0) * conversionFactor,
        min_drawal_date: item.date_of_min_drawal,
        max_drawal_date: item.date_of_max_drawal,
        min_schedule_date: item.date_of_min_schedule,
        max_schedule_date: item.date_of_max_schedule,
      }));

      processedData.sort((a, b) => a.slot - b.slot);

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);
      setChartKeys([]);
    } else {
      let filteredDiscomData = discomData;

      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredDiscomData = filteredDiscomData.filter((item) =>
          selectedDiscom.includes(item.discom),
        );
      }

      const slotMap = new Map<number, any>();

      filteredDiscomData.forEach((item) => {
        const slot = item.slot;
        const discom = item.discom;

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }

        const slotData = slotMap.get(slot);

        slotData[`${discom}_min_drawal`] = item.min_drawal * conversionFactor;
        slotData[`${discom}_max_drawal`] = item.max_drawal * conversionFactor;
        slotData[`${discom}_avg_drawal`] = item.avg_drawal * conversionFactor;
        slotData[`${discom}_mode_drawal_window_formula`] =
          item.mode_drawal_window_formula * conversionFactor;
        slotData[`${discom}_min_schedule`] =
          item.min_schedule * conversionFactor;
        slotData[`${discom}_max_schedule`] =
          item.max_schedule * conversionFactor;
        slotData[`${discom}_avg_schedule`] =
          item.avg_schedule * conversionFactor;
        slotData[`${discom}_mode_schedule_window_formula`] =
          item.mode_schedule_window_formula * conversionFactor;
        slotData[`${discom}_min_drawal_date`] = item.date_of_min_drawal;
        slotData[`${discom}_max_drawal_date`] = item.date_of_max_drawal;
        slotData[`${discom}_min_schedule_date`] = item.date_of_min_schedule;
        slotData[`${discom}_max_schedule_date`] = item.date_of_max_schedule;
      });

      const processedData = Array.from(slotMap.values());
      processedData.sort((a, b) => a.slot - b.slot);

      let finalData = processedData;
      if (selectedBlock.length > 0) {
        finalData = finalData.filter((item) =>
          selectedBlock.map((b) => b.value).includes(item.slot),
        );
      }
      setChartData(finalData);
      setTableData(finalData);

      const uniqueDiscoms = Array.from(
        new Set(filteredDiscomData.map((item) => item.discom)),
      ).sort();
      setChartKeys(uniqueDiscoms);
    }
  }, [
    summaryData,
    discomData,
    totalDiscomData,
    selectedUnit,
    selectedGroupKeys,
    selectedDiscom,
    selectedBlock,
  ]);

  const legends = useMemo(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      return [
        {
          dataKey: "min_demand",
          name: "Min Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
          dateKey: "min_demand_date",
        },
        {
          dataKey: "max_demand",
          name: "Max Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
          dateKey: "max_demand_date",
        },
        {
          dataKey: "avg_demand",
          name: "Average Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "mode_demand_window_formula",
          name: "Mode Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
      ];
    } else if (selectedGroupKeys === "Total Discom Load") {
      const legends: any[] = [];

      selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
        if (suffix === "schedule") {
          legends.push(
            {
              dataKey: "min_schedule",
              name: "Min Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "min_schedule_date",
            },
            {
              dataKey: "max_schedule",
              name: "Max Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "max_schedule_date",
            },
            {
              dataKey: "avg_schedule",
              name: "Average Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "mode_schedule_window_formula",
              name: "Mode Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
          );
        }

        if (suffix === "drawal") {
          legends.push(
            {
              dataKey: "min_drawal",
              name: "Min Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "min_drawal_date",
            },
            {
              dataKey: "max_drawal",
              name: "Max Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey: "max_drawal_date",
            },
            {
              dataKey: "avg_drawal",
              name: "Average Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "mode_drawal_window_formula",
              name: "Mode Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
          );
        }
      });

      return legends;
    } else {
      const legends: any[] = [];
      const metricTypes = [
        { field: "min", displayName: "Min", hasDate: true },
        { field: "max", displayName: "Max", hasDate: true },
        { field: "avg", displayName: "Average", hasDate: false },
        { field: "mode", displayName: "Mode", hasDate: false },
      ];

      chartKeys.forEach((discom, discomIndex) => {
        selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
          metricTypes.forEach((metric) => {
            const fieldSuffix =
              metric.field === "mode"
                ? `mode_${suffix}_window_formula`
                : `${metric.field}_${suffix}`;
            const dataKey = `${discom}_${fieldSuffix}`;
            const legendName = `${discom} ${metric.displayName} ${displayName}`;
            const dateKey = metric.hasDate
              ? `${discom}_${metric.field}_${suffix}_date`
              : undefined;

            legends.push({
              dataKey,
              name: legendName,
              legendColor: undefined,
              stroke: undefined,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
              dateKey,
            });
          });
        });
      });

      return legends;
    }
  }, [selectedGroupKeys, selectedChartType, selectedSuffix, chartKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      let displaySeriesName = seriesName;
      const matchingLegend = legends.find((l) => l.name === seriesName);
      if (matchingLegend?.dateKey && dataPoint?.[matchingLegend.dateKey]) {
        displaySeriesName = `${seriesName} (${dataPoint[matchingLegend.dateKey]})`;
      }
      return displaySeriesName;
    },
    [legends],
  );

  const getExtraKeys = () => ["slot_number", "Date"];
  const getExtraLabels = () => ["Block", "Date"];

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: getExtraKeys(),
    extraLabels: getExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Discom Load Summary Demo",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Discom"
              items={DISCOM.map((item) => item.label)}
              selectedItems={selectedDiscom}
              // ▶ UPDATED — added onSelect noop + Total Discom Load guard (from original)
              onSelect={(item, index) => {}}
              setSelectedItems={(item) => {
                if (selectedGroupKeys === "Total Discom Load") {
                  dispatch(setLoadingFilterName(""));
                  return;
                }
                onDiscomChange(item);
              }}
            />
          )}

          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />

          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={TYPE}
              selectedItems={selectedSuffix.map(
                (item) => item?.displayName || "",
              )}
              setSelectedItems={(selected: string[]) => {
                onTypeChange(selected);
              }}
            />
          )}

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Discom Load Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV({ ...tablePar });
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedGroupKeys}-${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={true}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  resolveDisplayName={resolveTooltipName}
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        {/* Demo footer — 2-col, no Source tooltip */}
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
